/**
 * MedDialog component
 *
 * Customizable dialog to use in the Medication List page.
 * Template renders differently depending on the dialog type (create med, update med, delete med).
 * Depending on the dialog type, carry out different actions as well.
 */

import { Component, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FormOption } from 'src/app/shared/models/form-option.model';
import { Medication } from 'src/app/calvin-emr-shared/models/medication.model';
import { ActionName } from 'src/app/shared/models/table-action.enum';
import { uniqueEntryValidator } from 'src/app/shared/directives/unique-entry.directive';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { NgIf, NgFor } from '@angular/common';
import { ButtonDirective } from 'primeng/button';
import { Select } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { EmrInputComponent } from "../../shared/emr-input/emr-input.component";
import { EmrCheckboxComponent } from "../../shared/emr-checkbox/emr-checkbox.component";

export interface MedicationDialogData {
  action: string;
  medication: Medication;
  ids: string[];
}

export interface RouteForm {
  routeName: string;
  otherName: string;
}

@Component({
  selector: 'emr-med-dialog',
  templateUrl: './med-dialog.component.html',
  styleUrls: ['./med-dialog.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    FormsModule,
    ReactiveFormsModule,
    ButtonDirective,
    NgFor,
    Select,
    InputTextModule,
    EmrInputComponent,
    EmrCheckboxComponent
  ],
})
export class MedicationDialogComponent implements OnInit {
  /**
   * The action which specifies what to display in the dialog.
   */
  action!: string;

  /**
   * List of medication ids passed from parent (medication-list) to validate id input.
   */
  ids!: string[];

  /**
   * Medication that is being added/edited/deleted.
   */
  med!: Medication;

  /**
   * Form group for the dialog's form.
   */
  form = this.fb.group({
    name: ['', Validators.required],
    id: ['', [Validators.required, Validators.minLength(4)]],
    routes: this.fb.array([]),
    controlledSubstance: [''],
    slidingScale: [''],
    mealTimes: [''],
  });

  /**
   * List of possible medication administration routes.
   */
  routes = [
    new FormOption('IM'),
    new FormOption('Inhalation'),
    new FormOption('IV'),
    new FormOption('NS'),
    new FormOption('Oral'),
    new FormOption('SL'),
    new FormOption('SQ'),
    new FormOption('Topical'),
    new FormOption('Rectal'),
    new FormOption('Other'),
  ];

  /**
   * Text to be displayed in the submit button.
   */
  btnLabel = '';

  /**
   * Assigns the dialog's properties values based on the action passed to it.
   */
  constructor(
    private fb: UntypedFormBuilder,
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig
  ) {
    this.action = config.data.action;
    this.med = config.data.medication;
    this.ids = config.data.ids;
  }

  ngOnInit(): void {
    switch (this.action) {
      case ActionName.Create: {
        this.btnLabel = 'Create';
        this.addRoute(); // Add a route to routes FormArray

        // Add validator to medication id to ensure it is unique
        this.idFormControl.addValidators([uniqueEntryValidator(this.ids)]);
        break;
      }
      case ActionName.Update: {
        this.btnLabel = 'Update';
        this.idFormControl.setValue(this.med.id);
        this.nameFormControl.setValue(this.med.name);
        this.controlledSubstanceFormControl.setValue(this.med.controlledSubstance);
        this.slidingScaleFormControl.setValue(this.med.slidingScale);
        this.mealTimesFormControl.setValue(this.med.mealTimes);

        // Set route controls
        this.med.getRoutes().forEach((route) => {
          this.addRoute(route);
        });

        break;
      }
      default: {
        this.btnLabel = 'Error';
      }
    }
  }

  /**
   * Generates a random ID for a new medication and fills the ID form
   * field with the value.
   */
  generateID() {
    const id = ('D9' + Date.now()).slice(0, 14);
    this.form.controls.id.setValue(id);
  }

  /**
   * Adds a route FormControl to the routes FormArray. Default values are empty strings,
   * but if name is specified, the FormControls will be pre-filled based on the name.
   * If name is "Other - ...", with the "..." being a custom route string, then the
   * otherName FormControl will be set to whatever "..." is.
   *
   * @param name route name as string
   */
  addRoute(name?: string) {
    // Defaults for a route that has not been filled in
    let rName = '';
    let oName = '';
    let disabled = true;

    // Get route name; if route name is "Other", get other name as well
    if (name) {
      const routeNames = name.split(' - ');
      rName = routeNames[0];

      if (rName == 'Other') {
        if (routeNames.length > 1) {
          oName = routeNames[1];
        }
        disabled = false;
      }
    }

    // Create form group to push to form array
    const routeForm = this.fb.group({
      routeName: [rName],
      otherName: [{ value: oName, disabled: disabled }],
    });
    this.routesFormArray.push(routeForm);
  }

  /**
   * Deletes the FormControl at the specified index.
   *
   * @param routeIndex the index of the route FormControl in the FormArray
   */
  deleteRoute(routeIndex: number) {
    this.routesFormArray.removeAt(routeIndex);
  }

  /**
   * Enables the secondary input if user selects "Other" as the route.
   *
   * @param fg FormGroup passed from template (assumed to be the FormGroups added to routes FormArray)
   */
  routeChanged(fg: AbstractControl) {
    if (this.isOtherRoute(fg)) {
      fg.get('otherName')?.enable();
    } else {
      fg.get('otherName')?.disable();
      fg.get('otherName')?.setValue('');
    }
  }

  /**
   * Checks if routeName contol is "Other".
   *
   * @param fg FormGroup (assumed to be the FormGroups added to routes FormArray)
   * @returns boolean value
   */
  isOtherRoute(fg: AbstractControl) {
    return fg.get('routeName')?.value === 'Other';
  }

  /**
   * Gets the RouteForm objects from the routes FormArray and
   * transforms them into strings.
   * If the route name is "Other" and the other route name is "...",
   * then they will be combined into one string: "Other - ..."
   *
   * @returns array of routes as strings
   *
   */
  getRoutes(): string[] {
    const routeObjs = this.form.get('routes')?.value;
    let routeStr = '';
    let routes: string[] = [];

    routes = routeObjs
      .map((obj: RouteForm) => {
        routeStr = obj.routeName;
        if (routeStr == 'Other') {
          routeStr += ' - ' + obj.otherName;
        }
        return routeStr;
      })
      .filter((routeStr: string) => {
        return routeStr != '';
      });

    return routes;
  }

  /**
   * Resets the dialog form.
   */
  resetDialog() {
    this.routesFormArray.clear();
    this.form.reset();
  }

  onCancel(): void {
    this.resetDialog();
    this.ref.close();
  }

  /**
   * Sets values of medication to pass back to caller based on the type of dialog that is open.
   */
  onSubmit(): void {
    // Set medication values based on form
    this.med.id = this.form.get('id')?.value;
    this.med.name = this.form.get('name')?.value;
    this.med.routes = this.getRoutes();
    this.med.controlledSubstance = this.form.get('controlledSubstance')?.value;
    this.med.slidingScale = this.form.get('slidingScale')?.value;
    this.med.mealTimes = this.form.get('mealTimes')?.value;
    this.resetDialog();
    this.ref.close(this.med);
  }

  get isCreateType(): boolean {
    return this.action == ActionName.Create;
  }

  get isEditType(): boolean {
    return this.action == ActionName.Update;
  }

  get hasIdError(): boolean {
    return this.idFormControl.invalid && (this.idFormControl.dirty || this.idFormControl.touched);
  }

  get hasIdRequiredError(): boolean {
    return this.idFormControl.errors?.required;
  }

  get hasIdMinLengthError(): boolean {
    return this.idFormControl.errors?.minlength;
  }

  get hasIdUniqueEntryError(): boolean {
    return this.idFormControl.errors?.uniqueEntry;
  }

  get isSubmitDisabled(): boolean {
    return !this.form.valid;
  }

  get routesFormArray(): UntypedFormArray {
    return this.form.controls.routes as UntypedFormArray;
  }

  get idFormControl(): UntypedFormControl {
    return this.form.controls.id as UntypedFormControl;
  }

  get nameFormControl(): UntypedFormControl {
    return this.form.controls.name as UntypedFormControl;
  }

  get controlledSubstanceFormControl(): UntypedFormControl {
    return this.form.controls.controlledSubstance as UntypedFormControl;
  }

  get slidingScaleFormControl(): UntypedFormControl {
    return this.form.controls.slidingScale as UntypedFormControl;
  }
  get mealTimesFormControl(): UntypedFormControl {
    return this.form.controls.mealTimes as UntypedFormControl;
  }
}
